/* eslint-disable max-len, camelcase */
import { find, map, matches, omit } from "lodash";
import AppContext from "PFApp/app_context";
import LegacyForm from "PFApp/components/form/legacy_form";
import { useGrowl } from "PFApp/use_growl";
import { Alert } from "PFComponents/alert";
import { LoadingDots } from "PFComponents/loading_dots";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useCreatePermissionsGroup } from "PFCore/hooks/queries/admin/use_create_permissions_group";
import { useEditPermissionsGroup } from "PFCore/hooks/queries/admin/use_edit_permissions_group";
import { useAdminBookingCategories } from "PFCore/hooks/queries/bookings/booking_categories/use_admin_booking_categories";
import { useAdminBookingCategoriesInvalidate } from "PFCore/hooks/queries/bookings/booking_categories/use_admin_booking_categories_invalidate";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import fetchCurrentAccount from "PFCore/services/accounts/current_account";
import { CURRENT_ACCOUNT_ACTIONS } from "PFReducers/current_account_reducer";
import { CustomType, FeatureFlag, Permission, PermissionRule, PermissionsGroup } from "PFTypes";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";
import { humanize } from "underscore.string";

import { AdminItemPage } from "../../components/admin_item_page";
import { RemovePermissionConfirmModal } from "./modal/remove_permission_confirm_modal";
import { setBookingCategoriesAccessLevel } from "./permissions_form_data";
import css from "./permissions_group_edit_page.module.scss";
import { Errors, FormData } from "./permissions_group_edit_page.types";
import PermissionsGroupItemsEdit from "./permissions_group_items_edit";
import { PermissionsGroupSection } from "./sections/permissions_group_section";
import { PermissionsSection } from "./sections/permissions_section";
import { useCrossGroupPermissions } from "./sections/use_cross_group_permissions";
import { PermissionGroupSketch, useFunctionalityPermissions } from "./sections/use_functionality_permissions";

type PermissionsGroupEditPageProps = {
  customTypes: CustomType[];
  permissionsGroup: PermissionsGroup;
  permissionsGroups: PermissionsGroup[];
} & RouteComponentProps;

const PermissionsGroupEditPage = ({
  customTypes,
  permissionsGroup,
  permissionsGroups
}: PermissionsGroupEditPageProps): JSX.Element => {
  const { dispatch } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();

  const history = useHistory();
  const growl = useGrowl();
  const { t } = useTranslation(["translation", "bookingModule"]);
  const { t: permissionsT } = useTranslation("admin", { keyPrefix: "permissionsGroups" });
  const isPermittedTo = useIsCurrentUserPermittedTo();

  const isNew = !permissionsGroup.id;

  const useSavePermissionsGroup = isNew ? useCreatePermissionsGroup : useEditPermissionsGroup;
  const { mutate: savePermissionsGroup } = useSavePermissionsGroup();

  const [formData, setFormData] = useState<FormData>({
    ...(isNew ? omit(permissionsGroup, "id") : permissionsGroup)
  });

  const [errors, setErrors] = useState<Errors | null>(null);

  const [displayRemovePermissionConfirm, setDisplayRemovePermissionConfirm] = useState(false);
  const [submitForm, setSubmitForm] = useState<(() => void) | null>(null);

  const isEnabled = useIsFeatureEnabled();
  const isBookingModuleEnabled = isEnabled(FeatureFlag.BookingModule);

  const { data: bookingCategories, isLoading } = useAdminBookingCategories(1, undefined, {
    enabled: isBookingModuleEnabled
  });
  const { invalidate: invalidateAdminBookingCategories } = useAdminBookingCategoriesInvalidate();
  const { invalidate: invalidateBookings } = useBookingInvalidate();

  const refreshAccount = async () => {
    const account = await fetchCurrentAccount();
    if (account) {
      dispatch({
        type: CURRENT_ACCOUNT_ACTIONS.CURRENT_ACCOUNT_READY,
        payload: account
      });
    }
  };

  const handleError = (errors) => {
    setErrors(errors);
    growl({
      kind: "error",
      message: t("translation:form.notSaved")
    });
  };

  const handleSuccess = () => {
    const { name } = formData;

    growl({
      message: permissionsT(isNew ? "groupCreated" : "groupUpdated", {
        permissionsGroupName: name
      })
    });

    history.push("/admin/permissions_groups", { trigger: true });
    invalidateAdminBookingCategories();
    invalidateBookings();
    refreshAccount();
  };

  const handleRemovePermissionConfirmOK = () => {
    submitForm && submitForm();
  };

  const handleRemovePermissionConfirmClose = () => {
    setDisplayRemovePermissionConfirm(false);
    setSubmitForm(null);
  };

  const filteredPermissions = useMemo(() => {
    const excludedKeys = [...(!isBookingModuleEnabled ? ["booking_resourcer"] : [])];

    return formData.permissions.filter((perm) => !excludedKeys.includes(perm.name));
  }, [formData.permissions, isBookingModuleEnabled]);

  const otherErrors = omit(
    errors,
    "display_as",
    "name",
    "permissions",
    "create_activity_templates",
    "cross_group_permissions"
  );
  const title = permissionsT(isNew ? "titleCreate" : "titleEdit");

  const permissionsGroupsList: PermissionGroupSketch[] = isNew
    ? [
        {
          id: "self",
          name: formData.name,
          cross_group_permissions: {},
          custom_types_access_levels: {
            activities: {},
            own_profile: {}
          },
          permissions: [],
          display_as: "",
          default: false
        },
        ...permissionsGroups
      ]
    : [...permissionsGroups];

  const { columns: functionalityPermissionsColumns, warnings: functionalityPermissionsWarnings } =
    useFunctionalityPermissions({
      formData,
      setFormData
    });

  const { columns: crossGroupPermissionsColumns, renderFocusRow } = useCrossGroupPermissions({
    formData,
    permissionsGroup,
    setFormData,
    permissionsGroupsList,
    customTypes
  });

  return (
    <>
      <AdminItemPage title={title} className={css.root}>
        <LegacyForm
          isNew={isNew}
          isDirty
          onSuccess={handleSuccess}
          onError={handleError}
          onCancel={() => history.push("/admin/permissions_groups")}
          onSubmit={(handleFormSuccess, handleFormError) => {
            setErrors(null);
            const permissionsGroupManagementWasJustRemoved =
              // it's the current user's pg
              currentProfile.permissions_group.id === permissionsGroup.id &&
              // was permitted
              isPermittedTo(PermissionRule.PermissionsGroupsManagement) &&
              // is no longer permitted (permission was just removed)
              find(permissionsGroup.permissions, (permission) =>
                matches(permission)({ name: PermissionRule.PermissionsGroupsManagement })
              )?.permitted === false;

            if (permissionsGroupManagementWasJustRemoved) {
              setDisplayRemovePermissionConfirm(true);
              setSubmitForm(() => {
                savePermissionsGroup(formData, {
                  onSuccess: handleFormSuccess,
                  onError: handleFormError
                });
              });
            } else {
              savePermissionsGroup(formData, {
                onSuccess: handleFormSuccess,
                onError: handleFormError
              });
            }
          }}
        >
          {otherErrors &&
            map(otherErrors, (value, key) => (
              <Alert kind="error" key={key} title={humanize(key)} style={{ marginBottom: 30 }}>
                {String(value)}
              </Alert>
            ))}

          <InputFieldSet
            value={formData.name}
            label={permissionsT("columns.name")}
            onChange={(name) => setFormData({ ...formData, name })}
            error={
              errors &&
              errors.name &&
              `${permissionsT("nameTip")} (${t("translation:error")}: ${errors.name})`
            }
            tip={permissionsT("nameTip")}
            qaId="permissions_group_name"
            style={{ marginBottom: 20 }}
          />

          <InputFieldSet
            value={formData.display_as}
            label={permissionsT("columns.displayAs")}
            onChange={(display_as) => setFormData({ ...formData, display_as })}
            error={errors && errors.display_as}
            qaId="permissions_group_display_as"
          />

          <div style={{ margin: 20 }} data-qa-id="default_toggle_section">
            <Toggle
              inline
              checked={formData.default}
              disabled={permissionsGroup.default}
              label={permissionsT("columns.default")}
              onChange={(val) => setFormData({ ...formData, default: val })}
            />
          </div>
          <PermissionsSection<Permission>
            title={permissionsT("permissions.functionality.title")}
            description={permissionsT("permissions.functionality.description")}
            qaId="DataTable.Permissions"
            columns={functionalityPermissionsColumns}
            data={filteredPermissions}
            warnings={functionalityPermissionsWarnings}
            errors={errors?.permissions}
          />
          <PermissionsSection<PermissionGroupSketch>
            title={permissionsT("permissions.crossGroupPermissions.title")}
            description={permissionsT("permissions.crossGroupPermissions.description")}
            qaId="DataTable.CrossGroupPermissions"
            columns={crossGroupPermissionsColumns}
            data={permissionsGroupsList}
            errors={errors?.cross_group_permissions}
            renderFocusRow={renderFocusRow}
          />
          <PermissionsGroupSection
            qaId="PermissionsGroupCTEdit"
            title={permissionsT("permissions.ownProfileGroupPermissions.title")}
            description={
              <>
                <p>{permissionsT("permissions.ownProfileGroupPermissions.description")}*</p>
                <p>
                  <small>*{permissionsT("permissions.ownProfileGroupPermissions.smallDescription")}</small>
                </p>
              </>
            }
            customTypes={customTypes}
            accessLevelType="own_profile"
            formData={formData}
            setFormData={setFormData}
            errors={errors?.custom_types_access_levels?.own_profile}
          />
          <PermissionsGroupSection
            qaId="PermissionsGroupCTEdit"
            title={permissionsT("permissions.activitiesGroupPermissions.title")}
            description={<p>{permissionsT("permissions.activitiesGroupPermissions.description")}*</p>}
            customTypes={customTypes}
            accessLevelType="activities"
            formData={formData}
            setFormData={setFormData}
            errors={errors?.custom_types_access_levels?.activities}
          />

          {isBookingModuleEnabled && (
            <>
              <h2 style={{ marginTop: 50 }}>{t("bookingModule:config.permissions.title")}</h2>
              <p>{t("bookingModule:config.permissions.description")}</p>
              {isLoading && (
                <div style={{ textAlign: "center" }}>
                  <LoadingDots circlesEnabled />
                </div>
              )}
              {!isLoading && (
                <>
                  {errors && errors.booking_categories_access_levels && (
                    <Alert kind="error" title={errors.booking_categories_access_levels} />
                  )}
                  {formData.booking_categories_access_levels && (
                    <PermissionsGroupItemsEdit
                      dataTableQAId="PermissionsGroupBookingCategoriesEdit"
                      items={bookingCategories?.entries ?? []}
                      idToPermLevel={formData.booking_categories_access_levels}
                      handleChange={(categoryId, level) =>
                        setFormData((formData) => ({
                          ...formData,
                          ...setBookingCategoriesAccessLevel(formData, categoryId, level)
                        }))
                      }
                      getDisplayAs={(category) => category.display_as}
                    />
                  )}
                </>
              )}
            </>
          )}
        </LegacyForm>
      </AdminItemPage>

      {displayRemovePermissionConfirm && (
        <RemovePermissionConfirmModal
          onConfirm={handleRemovePermissionConfirmOK}
          onClose={handleRemovePermissionConfirmClose}
        />
      )}
    </>
  );
};

export default withRouter(PermissionsGroupEditPage);
